
import { useState, FormEvent } from 'react';

const alertCategories = [
  { id: 'city-council', label: 'City Council', icon: 'ri-government-line' },
  { id: 'schools', label: 'Schools', icon: 'ri-school-line' },
  { id: 'development', label: 'Development', icon: 'ri-building-2-line' },
  { id: 'public-safety', label: 'Public Safety', icon: 'ri-shield-check-line' },
  { id: 'business', label: 'Business', icon: 'ri-store-2-line' },
  { id: 'community', label: 'Community', icon: 'ri-group-line' },
  { id: 'sports', label: 'Sports', icon: 'ri-trophy-line' },
];

export default function BreakingNewsSignup() {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedCategories.length === alertCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(alertCategories.map((c) => c.id));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedCategories.length === 0) return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    const selectedLabels = selectedCategories
      .map((id) => alertCategories.find((c) => c.id === id)?.label)
      .filter(Boolean)
      .join(', ');
    formData.set('categories', selectedLabels);

    setIsSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6bpomitehdqnvnpi3t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(formData.entries() as Iterable<[string, string]>)
        ).toString(),
      });
      setIsSuccess(true);
      form.reset();
      setSelectedCategories([]);
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mt-16">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-xl">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left - Info */}
          <div className="p-8 lg:p-10 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-full mb-5 w-fit">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-300 text-xs font-bold uppercase tracking-wider">
                Breaking News Alerts
              </span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">
              Never Miss a <span className="text-teal-400">Breaking Story</span>
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Get instant email notifications when breaking news happens in Roseville. Choose the categories that matter most to you and stay informed on your terms.
            </p>
            <div className="flex items-center gap-6 text-gray-400 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-mail-check-line text-teal-400"></i>
                Instant delivery
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-shield-check-line text-teal-400"></i>
                No spam, ever
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-logout-circle-line text-teal-400"></i>
                Unsubscribe anytime
              </span>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white/5 backdrop-blur-sm p-8 lg:p-10">
            {isSuccess ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <div className="w-14 h-14 bg-teal-500/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-check-double-line text-2xl text-teal-400"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">You&apos;re All Set!</h3>
                <p className="text-gray-400 text-sm max-w-xs">
                  You&apos;ll receive breaking news alerts for your selected categories. Check your inbox for a confirmation email.
                </p>
              </div>
            ) : (
              <form id="breaking-news-alerts-form" data-readdy-form onSubmit={handleSubmit}>
                {/* Category Selection */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-white">Select Categories</label>
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-xs text-teal-400 hover:text-teal-300 font-medium cursor-pointer whitespace-nowrap"
                    >
                      {selectedCategories.length === alertCategories.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {alertCategories.map((cat) => {
                      const isSelected = selectedCategories.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => toggleCategory(cat.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'bg-teal-500 text-white shadow-md shadow-teal-500/30'
                              : 'bg-white/10 text-gray-300 hover:bg-white/20'
                          }`}
                        >
                          <i className={`${cat.icon} text-sm`}></i>
                          {cat.label}
                          {isSelected && <i className="ri-check-line text-sm"></i>}
                        </button>
                      );
                    })}
                  </div>
                  {selectedCategories.length === 0 && (
                    <p className="text-[11px] text-red-400 mt-2">
                      Please select at least one category
                    </p>
                  )}
                </div>

                <input
                  type="hidden"
                  name="categories"
                  value={selectedCategories
                    .map((id) => alertCategories.find((c) => c.id === id)?.label)
                    .filter(Boolean)
                    .join(', ')}
                />

                {/* Email + Name */}
                <div className="space-y-3 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your name"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || selectedCategories.length === 0}
                  className="w-full px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Subscribing...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <i className="ri-notification-3-line"></i>
                      Subscribe to Alerts
                    </span>
                  )}
                </button>

                <p className="text-[10px] text-gray-500 text-center mt-3">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
